import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Operator,
  PaginatedListProduct,
  Product,
  SortDirection,
} from "../../../services/swagger";
import { useEffect, useState, useTransition } from "react";
import { useTranslation } from "react-i18next";
import SearchBar from "../../atoms/SearchBar";
import { ProductService } from "../../../services/api";
import { prepareFilterString } from "../../../helpers/filters";
import SortingArrow from "../../atoms/SortingArrow";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import useBreakpoint from "../../../hooks/useBreakpoint";

interface ProductSelectorDialogProps {
  open: boolean;
  onSelect: (products: Product[]) => any;
  onClose: () => any;
  ignoreCodes?: string[];
}

const rowsPerPage = 10;

export default function ProductSelectorDialog(
  props: ProductSelectorDialogProps
) {
  const { t } = useTranslation("Product");
  const [isPending, startTransition] = useTransition();
  const [products, setProducts] = useState<PaginatedListProduct>({
    count: 0,
    docs: [],
  });
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [sortField, setSortField] = useState("totalAmountUsed");
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    SortDirection.Desc
  );
  const [displayAmountUsed, setDisplayAmountUsed] = useState(false);
  const breakpoint = useBreakpoint();

  const handlePageChange = (event: any, page: number) => {
    setPage(page - 1);
    // listRef.current?.scroll({ top: 0 });
  };

  const loadProducts = () => {
    startTransition(async () => {
      const res = await ProductService.getProductList({
        search: search,
        limit: rowsPerPage,
        skip: page * rowsPerPage,
        filters: prepareFilterString(
          props.ignoreCodes?.map((code) => ({
            field: "itemCode",
            operator: Operator.Ne,
            type: "string",
            value: code,
          })) || []
        ),
        sortField,
        sortDirection,
      });
      if (res?.data?.data) setProducts(res?.data?.data);
    });
  };

  useEffect(() => {
    if (products.docs.some((product) => product.source === "SAP"))
      setDisplayAmountUsed(true);
    else setDisplayAmountUsed(false);
  }, [products]);

  const handleClickSort = (field: string) => {
    if (field !== sortField) {
      setSortDirection(SortDirection.Desc);
      setSortField(field);
      return;
    }
    if (sortDirection === SortDirection.None)
      setSortDirection(SortDirection.Desc);
    if (sortDirection === SortDirection.Desc)
      setSortDirection(SortDirection.Asc);
    if (sortDirection === SortDirection.Asc)
      setSortDirection(SortDirection.Desc);
  };

  const handleSelectProduct = (product: Product) => {
    const index = selectedProducts.findIndex((e) => e._id === product._id);
    if (index > -1) {
      const newSelectedProducts = [...selectedProducts];
      newSelectedProducts.splice(index, 1);
      setSelectedProducts(newSelectedProducts);
      return;
    }
    setSelectedProducts([...selectedProducts, product]);
  };

  const handleClearSelectedProducts = () => {
    if (selectedProducts.length > 0) setSelectedProducts([]);
  };

  const handleSubmitProducts = () => {
    props.onSelect([...selectedProducts]);
    setSelectedProducts([]);
  };

  useEffect(() => {
    if (!props.open) return;
    loadProducts();
  }, [props.open, search, sortField, sortDirection, page]);

  useEffect(() => {
    if (products.count < 1) return;
    if (products.count < page * rowsPerPage) setPage(0);
  }, [products]);

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      fullScreen={!breakpoint.sm}
    >
      <DialogContent sx={{ p: { xs: 1, md: 2 }, display: "flex" }}>
        <Stack
          spacing={2}
          sx={{
            minWidth: { xs: 200, md: 400 },
          }}
        >
          <SearchBar
            onSearch={(text) => setSearch(text)}
            placeholder={t("Products")}
            alwaysHighlighted
          />
          <TableContainer sx={{ flexGrow: 1 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell width={20} sx={{ px: 1 }}>
                    <IconButton
                      color={
                        selectedProducts.length > 0 ? "primary" : undefined
                      }
                      onClick={handleClearSelectedProducts}
                    >
                      {selectedProducts.length > 0 ? (
                        <IndeterminateCheckBoxIcon />
                      ) : (
                        <CheckBoxOutlineBlankIcon />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell sx={{ px: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography fontSize="inherit">
                        {t("ItemCode")}
                      </Typography>
                      <SortingArrow
                        onClick={() => handleClickSort("itemCode")}
                        activeField={sortField}
                        field="itemCode"
                        sort={sortDirection as SortDirection}
                      />
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography fontSize="inherit">{t("Name")}</Typography>
                      <SortingArrow
                        onClick={() => handleClickSort("name")}
                        activeField={sortField}
                        field="name"
                        sort={sortDirection as SortDirection}
                      />
                    </Stack>
                  </TableCell>
                  {breakpoint.sm && (
                    <TableCell>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="flex-end"
                        spacing={1}
                      >
                        <Typography fontSize="inherit">{t("Price")}</Typography>
                        <SortingArrow
                          onClick={() => handleClickSort("price")}
                          activeField={sortField}
                          field="price"
                          sort={sortDirection as SortDirection}
                        />
                      </Stack>
                    </TableCell>
                  )}
                  {displayAmountUsed && (
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography fontSize="inherit">
                          {t("AmountUsed")}
                        </Typography>
                        <SortingArrow
                          onClick={() => handleClickSort("totalAmountUsed")}
                          activeField={sortField}
                          field="totalAmountUsed"
                          sort={sortDirection as SortDirection}
                        />
                      </Stack>
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {products.docs.map((product) => {
                  const selected = selectedProducts.some(
                    (p) => p._id === product._id
                  );
                  return (
                    <TableRow key={product._id} hover>
                      <TableCell sx={{ px: 1 }}>
                        <IconButton
                          color={selected ? "primary" : undefined}
                          onClick={() => handleSelectProduct(product)}
                        >
                          {selected ? (
                            <CheckBoxIcon />
                          ) : (
                            <CheckBoxOutlineBlankIcon />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ px: 0 }}>{product.itemCode}</TableCell>
                      <TableCell>{product.name}</TableCell>
                      {breakpoint.sm && (
                        <TableCell align="right" width={100}>
                          {product.price.toFixed(2)} {product.currency}
                        </TableCell>
                      )}
                      {displayAmountUsed && (
                        <TableCell width={1}>
                          {product.totalAmountUsed}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Pagination
            color="primary"
            count={Math.ceil(products.count / rowsPerPage)}
            page={page + 1}
            onChange={handlePageChange}
            shape="rounded"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose} variant="outlined" color="error">
          {t("Cancel")}
        </Button>
        <Tooltip
          placement="top"
          title={selectedProducts.map((product) => product.itemCode).join(",")}
        >
          <Button
            variant="contained"
            disabled={selectedProducts.length === 0}
            onClick={handleSubmitProducts}
          >
            {t("Select")} ({selectedProducts.length})
          </Button>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
}
